/**
 * "Still signed in" on the marketing site (owner, 2026-10-30: "if you go to
 * the main website it should know you're logged in still").
 *
 * The login lives here, in this portal's localStorage, which aperiomobility.com
 * cannot read. So while someone is signed in, the portal also sets a small
 * hint cookie on .aperiomobility.com: a version, the account kind and a first
 * name. It is NOT a credential (no token, nothing the API accepts): the site's
 * header (aperio/site/src/static/js/site.js "signed in") only uses it to show
 * the account button, whose links come back here, where the real login is
 * checked as always. Every way of signing out clears the auth state, and the
 * store subscriber in index.tsx then drops the cookie; /auth/logout drops it
 * itself before leaving the page.
 */

export const SITE_SESSION_COOKIE = 'aperio_who';
const DOMAIN = '.aperiomobility.com';
/** Refreshed on every portal page while signed in. */
const MAX_AGE = 60 * 60 * 24 * 30;

export type SiteAccountKind = 'customer' | 'fleet' | 'driver' | 'staff' | 'shop';

const STAFF = ['manager', 'technician', 'tester', 'sales'];

/** The same order as Login.tsx's landing page and RequiredCustomer. */
export function siteAccountKind(roles: unknown): SiteAccountKind {
  const r = Array.isArray(roles) ? roles.map(String) : [];
  if (r.some((role) => STAFF.includes(role))) return 'staff';
  if (r.includes('fleet_manager')) return 'fleet';
  if (r.includes('fleet_driver')) return 'driver';
  if (r.includes('partner_shop')) return 'shop';
  return 'customer';
}

/** First word of the account's name, trimmed for a header button. */
export function siteFirstName(name: unknown): string {
  const first = String(name ?? '').trim().split(/\s+/)[0] || '';
  // eslint-disable-next-line no-control-regex
  return first.replace(/[\u0000-\u001f\u007f<>]/g, '').slice(0, 30);
}

type Auth = { isAuthenticated?: boolean; currentUser?: { [key: string]: any } };

/** The cookie's value for this auth state, or null when signed out. */
export function siteSessionValue(auth: Auth): string | null {
  if (!auth?.isAuthenticated || !auth.currentUser?.token) return null;
  return encodeURIComponent(JSON.stringify({
    v: 1,
    k: siteAccountKind(auth.currentUser.roles),
    n: siteFirstName(auth.currentUser.name),
  }));
}

const onAperio = (host: string) => /(^|\.)aperiomobility\.com$/i.test(host);

let written: string | null | undefined;

/** Writes or clears the hint to match `auth`. Only on aperiomobility.com. */
export function syncSiteSession(
  auth: Auth,
  host: string = typeof window !== 'undefined' ? window.location.hostname : '',
): void {
  if (typeof document === 'undefined' || !onAperio(host)) return;
  const value = siteSessionValue(auth);
  // Re-written once per page load (refreshes Max-Age), then only on change.
  if (value === written) return;
  written = value;
  document.cookie = value
    ? `${SITE_SESSION_COOKIE}=${value}; Max-Age=${MAX_AGE}; Path=/; Domain=${DOMAIN}; Secure; SameSite=Lax`
    : `${SITE_SESSION_COOKIE}=; Max-Age=0; Path=/; Domain=${DOMAIN}; Secure; SameSite=Lax`;
}

/**
 * Where /auth/logout sends the visitor back to: only an https page on
 * aperiomobility.com (never an open redirect), else null.
 */
export function safeLogoutNext(next: string | null): string | null {
  if (!next) return null;
  try {
    const url = new URL(next);
    if (url.protocol !== 'https:' || !onAperio(url.hostname)) return null;
    return url.toString();
  } catch (e) {
    return null;
  }
}
